/**
 * Vendor self-serve scoped rebates (flag `vendor_self_serve`): the vendor's own
 * rebate programs, the stores it can scope one to, and creating one.
 * See `/vendor/rebate-programs` on the API.
 */
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';

export type VendorRebateProgramStatus = 'draft' | 'pending_store_approval' | 'active' | 'declined' | 'expired';

export interface VendorRebateProgram {
  id: string;
  name: string;
  brand: string;
  kind: 'instant_rebate' | 'trade_credit' | 'bundle';
  source: string;
  organizationId: string | null;
  organizationName: string | null;
  scope: { kind: 'all' | 'location' | 'integration' | 'zone'; locationId?: string | null; integrationId?: string | null };
  startAt: string | null;
  endAt: string | null;
  status: VendorRebateProgramStatus;
}

export interface VendorStoreOption {
  organizationId: string;
  name: string;
  locations: Array<{ id: string; name: string; isPrimary: boolean }>;
  integrations: Array<{ id: string; platform: string | null; name: string }>;
}

export interface CreateScopedRebateInput {
  name: string;
  termsKind: 'instant_rebate' | 'trade_credit';
  amount: number;
  startAt: string;
  endAt: string;
  storeOrganizationId?: string;
  scope?: { kind: 'all' } | { kind: 'location'; locationId: string } | { kind: 'integration'; integrationId: string };
}

export interface ScopedRebateStoreResult {
  storeOrganizationId: string;
  outcome: 'created' | 'skipped';
  programId?: string;
  reason?: string;
}

async function call<T>(path: string, init?: { method?: string; body?: unknown }): Promise<T> {
  const res = await makeApiCall(path, {
    method: init?.method || 'GET',
    headers: await getAuthHeaders(),
    ...(init?.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload?.error?.message || payload?.message || 'Request failed');
  return payload?.data as T;
}

export const VENDOR_REBATE_PROGRAMS_QUERY_KEY = ['vendor-rebate-programs'] as const;
export const VENDOR_REBATE_STORES_QUERY_KEY = ['vendor-rebate-stores'] as const;
export const listVendorRebatePrograms = (): Promise<VendorRebateProgram[]> => call('/vendor/rebate-programs');
export const listVendorRebateStores = (): Promise<VendorStoreOption[]> => call('/vendor/rebate-programs/stores');
export const createScopedRebate = (input: CreateScopedRebateInput): Promise<ScopedRebateStoreResult[]> =>
  call('/vendor/rebate-programs', { method: 'POST', body: input });
